import React, {
    ContextType,
    FC,
    type Key,
    PropsWithChildren,
    use,
    useCallback,
    useDeferredValue,
    useEffect,
    useMemo,
    useState,
} from "react";
import { DmsDefaultSubType, DmsType, ITableDocument, TBindSubType } from "@dms/types";
import { ALL_DOCUMENTS } from "@dms/configs/constants";
import { DmsUtils } from "@dms/scripts/utils/DmsUtils";
import { checkIsGroupType } from "@dms/scripts/helpers/checkIsGroupType";
import { DmsAppContext, DmsAppControlContext, DmsDataContext } from "@dms/types/ContextTypes";
import { DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { DocumentsApi } from "@dms/scripts/DocumentsApi";
import { useGqlMutator } from "../../../scripts/graphql/useGqlMutator";
import type { DragStartEvent } from "@dnd-kit/core/dist/types";

export const DmsAppProvider: FC<PropsWithChildren> = ({ children }) => {
    const mutator = useGqlMutator();
    const initActiveType: string[] = useMemo(
        () => JSON.parse(localStorage.getItem("keyPath")) ?? [DmsType.new_documents],
        []
    );

    const initialOpenKey = useMemo(() => {
        const { type } = DmsUtils.getActiveTypeAndSubType(initActiveType);
        if (checkIsGroupType(type)) {
            return initActiveType;
        }
        return [""];
    }, [initActiveType]);
    const [isOpenSettingsCol, setIsOpenSettingsCol] = useState(false);
    const [activeType, setActiveType] = useState<string[]>([DmsType.new_documents]);
    const [view, setView] = useState<ContextType<typeof DmsAppContext>["view"]>("list");
    const [isTypeChangeMode, setIsTypeChangeMode] = useState<boolean>(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [focusedRow, setFocusedRow] = useState(null);
    const [isDraggingRightNow, setIsDraggingRightNow] = useState(false);
    const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);
    const [downloadDocumentsModalOpen, setDownloadDocumentsModalOpen] = useState(false);
    const [bindModalData, setBindModalData] = useState<{
        isOpen: boolean;
        id?: string;
        type?: TBindSubType;
        currentVal?: string;
    }>({
        isOpen: false,
    });
    const [editTypeModalData, setEditTypeModalData] = useState<{
        isOpen: boolean;
        id?: string;
        type?: TBindSubType;
        currentVal?: string;
    }>({ isOpen: false });
    const [openedMenuItemKeys, setOpenedMenuItemKeys] = useState(initialOpenKey);
    const [searchValue, setSearchValue] = useState<string>("");
    const { documents, documentsKV } = use(DmsDataContext);
    const deferredView = useDeferredValue(view);

    // Configure sensors for drag detection
    const mouseSensor = useSensor(MouseSensor, {
        // Require the mouse to move by 10 pixels before activating
        activationConstraint: {
            distance: 10,
        },
    });
    const touchSensor = useSensor(TouchSensor, {
        // Press delay of 250ms, with tolerance of 5px of movement
        activationConstraint: {
            delay: 250,
            tolerance: 5,
        },
    });
    const sensors = useSensors(mouseSensor, touchSensor);

    const { type, subType } = DmsUtils.getActiveTypeAndSubType(activeType);

    const folderDocuments: ITableDocument[] = useMemo(() => {
        return (documents ?? []).filter(el => {
            if (type === ALL_DOCUMENTS) {
                return true;
            }

            if (subType?.includes(DmsDefaultSubType.all_subTypes)) {
                return el.type === type;
            }

            if (subType?.includes(DmsDefaultSubType.no_subTypes)) {
                return el.type === type && !el.subType;
            }

            return el.type === type && el.subType === subType;
        });
    }, [documents, subType, type]);

    useEffect(() => {
        localStorage.removeItem("keyPath");
    }, [activeType]);

    useEffect(() => {
        const folderDocumentKeys = new Set(folderDocuments.map(doc => doc.key));
        setSelectedRowKeys(prev => {
            return prev.filter(key => folderDocumentKeys.has(String(key)));
        });
    }, [folderDocuments]);

    const value: ContextType<typeof DmsAppContext> = useMemo(
        () => ({
            folderDocuments,
            view: deferredView,
            activeType,
            isTypeChangeMode,
            selectedRowKeys,
            isDeleteMode,
            isDraggingRightNow,
            downloadDocumentsModalOpen,
            openedMenuItemKeys,
            isOpenSettingsCol,
            searchValue,
            focusedRow,
            bindModalData,
            editTypeModalData,
        }),
        [
            activeType,
            bindModalData,
            downloadDocumentsModalOpen,
            editTypeModalData,
            focusedRow,
            folderDocuments,
            isDeleteMode,
            isDraggingRightNow,
            isOpenSettingsCol,
            isTypeChangeMode,
            openedMenuItemKeys,
            searchValue,
            selectedRowKeys,
            deferredView,
        ]
    );

    const action: ContextType<typeof DmsAppControlContext> = useMemo(
        () => ({
            resetAppContext: () => {
                setIsOpenSettingsCol(false);
                setActiveType([DmsType.new_documents]);
                setView("list");
                setIsTypeChangeMode(false);
                setSelectedRowKeys([]);
                setIsDraggingRightNow(false);
                setIsDeleteMode(false);
                setDownloadDocumentsModalOpen(false);
                setOpenedMenuItemKeys([""]);
                setSearchValue("");
                setFocusedRow(null);
                // setLoadDataSource(false);
            },
            selectRow: (key: Key) => {
                setSelectedRowKeys(prevState => {
                    return [...prevState, key];
                });
            },
            selectRows: setSelectedRowKeys,
            resetSelectedRows: () => {
                setSelectedRowKeys([]);
            },
            unselectRow: (key: Key) => {
                setSelectedRowKeys(prev => {
                    return prev.filter(el => el !== key);
                });
            },
            setActiveType,
            setViewMode: setView,
            typeChangeModeToggle: setIsTypeChangeMode,
            deleteModeToggle: setIsDeleteMode,
            toggleDraggingState: setIsDraggingRightNow,
            toggleDownloadDocumentsModal: setDownloadDocumentsModalOpen,
            setOpenedMenuItemKeys,
            toggleOpenMenuItem: (key: string) => {
                setOpenedMenuItemKeys(prev => {
                    if (prev.includes(key)) {
                        return [];
                    } else {
                        return [key];
                    }
                });
            },
            toggleIsOpenSettingsCol: setIsOpenSettingsCol,
            setSearchValue,
            // setLoadDataSource,
            setFocusedRow,
            bindModalDataAction: setBindModalData,
            editTypeModalDataAction: setEditTypeModalData,
        }),
        []
    );

    const onDragStart = useCallback((event: DragStartEvent) => {
        const { active } = event;
        if (active?.data?.current?.documentIds?.length) {
            setIsDraggingRightNow(true);
        }
    }, []);
    const onDragEnd = useCallback(
        (event: DragEndEvent) => {
            setIsDraggingRightNow(false);
            // Handle drop event
            const { active, over } = event;

            if (active && over) {
                setSelectedRowKeys([]);
                setFocusedRow(null);

                const documentIds: string[] = active?.data?.current?.documentIds ?? [];
                const list = documentIds.map(id => documentsKV[id]).filter(Boolean);
                const destination: string[] = over.data.current?.destinationFolderKeyPath;
                if (!destination || !list.length) {
                    return;
                }
                if (destination[0] === DmsType.trash) {
                    DocumentsApi.sendToTrash(list, mutator);
                } else {
                    DocumentsApi.changeDocumentsType(
                        list.map(el => ({ ...el, ...DmsUtils.getActiveTypeAndSubType(destination) })),
                        mutator
                    );
                }
            }
        },
        [documentsKV, mutator]
    );

    return (
        <DmsAppContext value={value}>
            <DmsAppControlContext value={action}>
                <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
                    {children}
                </DndContext>
            </DmsAppControlContext>
        </DmsAppContext>
    );
};
